/**
 * Content Block Renderer (Issue #933)
 *
 * Renders individual content blocks based on type.
 * Uses Layer 2 CSS tokens per ORO design system.
 *
 * Supported block types:
 * - paragraph: Simple text paragraph
 * - list: Bulleted or numbered list
 * - callout: Highlighted info/warning box
 * - heading: Sub-heading (h2/h3/h4)
 */

import type { ContentBlock } from '../../types/cms';
import CalloutCard from '../CalloutCard';

interface ContentBlockRendererProps {
  block: ContentBlock;
}

export function ContentBlockRenderer({ block }: ContentBlockRendererProps) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="text-text-secondary leading-relaxed">
          {block.text}
        </p>
      );

    case 'list': {
      const ListTag = block.ordered ? 'ol' : 'ul';
      const listClass = block.ordered ? 'list-decimal' : 'list-disc';
      return (
        <ListTag className={`${listClass} pl-5 space-y-1.5 text-text-secondary`}>
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ListTag>
      );
    }

    case 'callout':
      return (
        <CalloutCard title={block.title} variant={block.variant}>
          {block.text}
        </CalloutCard>
      );

    case 'heading': {
      const Tag = `h${block.level}` as 'h2' | 'h3' | 'h4';
      const headingStyles = {
        2: 'text-lg font-bold text-text-primary',
        3: 'text-base font-semibold text-text-primary',
        4: 'text-sm font-semibold text-text-primary',
      }[block.level];
      return <Tag className={headingStyles}>{block.text}</Tag>;
    }

    default:
      // TypeScript exhaustive check
      return null;
  }
}
